import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDownIcon, PanelLeftCloseIcon, PanelLeftOpenIcon } from 'lucide-react';
import { cn } from '../cn';
import { Heading } from './primitives';
import { BackLink, Breadcrumbs, type BreadcrumbItem } from './navigation';

/** One destination in the navigation. */
export interface ShellNavItem {
  readonly to: string;
  readonly label: string;
  /** Single glyph, matching the mockup's typographic icons. */
  /**
   * A lucide icon element, e.g. `<HomeIcon />`. Deliberately not a character:
   * typographic glyphs resolve to whatever font the device substitutes, which
   * is why the nav looked wrong on phones.
   */
  readonly icon: React.ReactNode;
  /**
   * Active only on this exact path, not on paths below it. Defaults to true
   * for `/`, which would otherwise match every page.
   */
  readonly end?: boolean | undefined;
}

/** Whether a nav item matches only its exact path. */
function isExact(item: ShellNavItem): boolean {
  return item.end ?? item.to === '/';
}

/**
 * Whether the current path is this item's page or a page below it, matched by
 * whole segments as NavLink does: `/teams` holds `/teams/7`, not `/teams-archive`.
 */
function holdsPath(item: ShellNavItem, pathname: string): boolean {
  if (pathname === item.to) return true;
  if (isExact(item)) return false;
  return pathname.startsWith(item.to.endsWith('/') ? item.to : `${item.to}/`);
}

/** A labelled group of nav entries. */
export interface ShellNavSection {
  /** Shown as a divider heading. Null for the first group, which needs none. */
  readonly label: string | null;
  readonly items: readonly ShellNavItem[];
  /**
   * Start folded, showing only the heading; the person opens it with a
   * click and the choice is remembered in this browser. A section holding
   * the current page is always open.
   */
  readonly defaultCollapsed?: boolean | undefined;
}

/** What the sidebar header shows. */
export interface ShellIdentity {
  /** The application or tenant name, rendered as text when there is no logo. */
  readonly name: string;
  readonly logoUrl?: string | null | undefined;
  /** Small uppercase line under the name, e.g. "TEAM EXPERIENCES". */
  readonly tagline?: string | undefined;
  /** Sidebar background. Defaults to the brand sidebar variable. */
  readonly sidebarColor?: string | undefined;
}

export interface ShellProps {
  readonly identity: ShellIdentity;
  readonly sections: readonly ShellNavSection[];
  /**
   * Renders the account control, shown at the right end of the top bar. The
   * variant callback shape is kept so applications stay agnostic of where the
   * shell places the menu.
   */
  readonly account?: ((variant: 'sidebar' | 'topbar') => React.ReactNode) | undefined;
  /** Optional widget above the account block, e.g. the concierge card. */
  readonly sidebarWidget?: React.ReactNode;
  /** Left half of the top bar, e.g. a scope picker. Hidden on phones. */
  readonly topbarLeft?: React.ReactNode;
  /** Right half of the top bar, e.g. the credits pill. Always visible. */
  readonly topbarRight?: React.ReactNode;
  /**
   * Optional panel docked to the right of the content column, below the top
   * bar. Content narrows to make room rather than being overlaid — the panel
   * and the page stay usable together (e.g. an assistant beside a form).
   */
  readonly aside?: React.ReactNode;
  /**
   * Optional full-width strip directly beneath the top bar, spanning the
   * content column edge to edge — e.g. an impersonation/read-only notice. It
   * scrolls with the top bar rather than the page, so it stays put as a mode
   * indicator.
   */
  readonly banner?: React.ReactNode;
  /**
   * Fills the content column with the children — no page padding, no max-width,
   * a fixed height below the top bar with the children owning their own scroll —
   * for a full-height app (e.g. the proposal editor) that must sit natively in
   * the frame and narrow beside the {@link ShellProps.aside} rather than scroll
   * as a document. Off by default; every ordinary page stays padded and scrolls.
   */
  readonly fillContent?: boolean;
  readonly children: React.ReactNode;
}

/**
 * The BCE application chrome: fixed sidebar, sticky top bar, content column.
 *
 * Transcribed from the portal mockup — 245px sidebar, 64px top bar, the same
 * active-state inset rule — and shared by every BCE application so they feel
 * like one product family. What varies between applications is data: the
 * identity block, the nav sections and the slot contents. The layout itself is
 * deliberately not configurable, because "almost the same shell" is how product
 * families drift apart.
 *
 * Below 720px (the mockup's own breakpoint) the sidebar is hidden — a 245px
 * rail on a 390px phone leaves an unusable sliver — and the top bar carries a
 * scrollable nav instead.
 *
 * @param props - Identity, navigation and slot content.
 * @returns The application shell.
 */
const FOLDED_KEY = 'portal.sidebar.sections';

/** Which foldable sections this browser has opened or closed, by label; true means open. */
function readFolded(): Readonly<Record<string, boolean>> {
  try {
    const raw = localStorage.getItem(FOLDED_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function writeFolded(value: Readonly<Record<string, boolean>>): void {
  try {
    localStorage.setItem(FOLDED_KEY, JSON.stringify(value));
  } catch {
    // A blocked store just forgets the choice on reload.
  }
}

const COLLAPSED_KEY = 'portal.sidebar.collapsed';

/** Whether this browser has collapsed the desktop sidebar to an icons-only rail. */
function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeCollapsed(value: boolean): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, String(value));
  } catch {
    // A blocked store just forgets the choice on reload.
  }
}

/** The identity header, nav sections and widget — shared by the fixed
 * desktop sidebar and the mobile drawer so they never drift. */
function SidebarBody({
  identity,
  sections,
  sidebarWidget,
  onNavigate,
  collapsed = false,
  onToggleCollapsed,
}: Pick<ShellProps, 'identity' | 'sections' | 'sidebarWidget'> & {
  readonly onNavigate?: (() => void) | undefined;
  /** Icons-only rail: labels, section headings and the tagline are hidden. */
  readonly collapsed?: boolean;
  /** When set, the collapse/expand toggle is shown at the foot of the rail. */
  readonly onToggleCollapsed?: (() => void) | undefined;
}): React.ReactElement {
  const { pathname } = useLocation();
  const [foldedState, setFoldedState] = useState<Readonly<Record<string, boolean>>>(readFolded);
  const folded = foldedState;
  const setFolded = (key: string, open: boolean): void => {
    const next = { ...foldedState, [key]: open };
    setFoldedState(next);
    writeFolded(next);
  };

  // Collapsed rail: every item is an icon with its label as a tooltip; section
  // headings become a thin divider (folding is meaningless without room for a
  // heading, so all items show).
  if (collapsed) {
    return (
      <>
        <div className="flex flex-col items-center border-b border-white/12 pb-[18px]">
          {identity.logoUrl ? (
            <img src={identity.logoUrl} alt={identity.name} className="h-[24px] w-auto object-contain" />
          ) : (
            <span className="text-[15px] leading-none font-bold tracking-[-1px]">{identity.name}</span>
          )}
        </div>

        <nav aria-label="Sections" className="flex flex-col items-center gap-[3px] pt-4">
          {sections.map((section, index) => {
            const key = section.label ?? 'primary';
            return (
              <div key={key} className="flex w-full flex-col items-center gap-[3px]">
                {index > 0 ? <div className="my-2 h-px w-6 bg-white/20" /> : null}
                {section.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={isExact(item)}
                    onClick={onNavigate}
                    title={item.label}
                    aria-label={item.label}
                    className={({ isActive }) =>
                      cn(
                        'grid size-9 place-items-center rounded-portal-button transition-colors [&>svg]:size-[18px]',
                        isActive
                          ? 'bg-white/14 text-white'
                          : 'text-white/78 hover:bg-white/14 hover:text-white',
                      )
                    }
                  >
                    <span aria-hidden="true" className="inline-flex [&>svg]:size-[18px]">
                      {item.icon}
                    </span>
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="flex-1" />

        {onToggleCollapsed ? (
          <button
            type="button"
            title="Expand sidebar"
            aria-label="Expand sidebar"
            onClick={onToggleCollapsed}
            className="mt-3 grid size-9 place-items-center self-center rounded-portal-button text-white/78 hover:bg-white/14 hover:text-white"
          >
            <PanelLeftOpenIcon aria-hidden className="size-[18px]" />
          </button>
        ) : null}
      </>
    );
  }

  return (
    <>
      <div className="border-b border-white/12 px-[10px] pb-[23px]">
        {identity.logoUrl ? (
          <img
            src={identity.logoUrl}
            alt={identity.name}
            className="h-[26px] w-auto object-contain"
          />
        ) : (
          <span className="text-[22px] leading-none font-bold tracking-[-1.5px]">
            {identity.name}
          </span>
        )}
        {identity.tagline === undefined ? null : (
          <small className="mt-2 block text-[8px] tracking-[2px] text-white/78">
            {identity.tagline}
          </small>
        )}
      </div>

      <nav aria-label="Sections" className="flex flex-col pt-4">
        {sections.map((section) => {
          const key = section.label ?? 'primary';
          const holdsCurrentPage = section.items.some((item) => holdsPath(item, pathname));
          const open = section.label === null || holdsCurrentPage || (folded[key] ?? !section.defaultCollapsed);
          return (
          <div key={key} className="flex flex-col gap-[3px]">
            {section.label === null ? null : (
              // A real heading, not a styled div, so the grouping is in the
              // accessibility tree and not only in the paint; a button inside
              // it when the section can fold.
              //
              // white/78 rather than a fainter tint: the sidebar is now the
              // brand's royal, and anything below ~78% white falls under 4.5:1
              // against it. Tenant sidebars are usually darker and so had
              // slack, but the contrast floor has to hold for the lightest
              // sidebar any brand can set, not the darkest.
              <h2 className="mt-4 mb-1 border-t border-white/20 pt-3 text-[8px] font-extrabold tracking-[1.6px] text-white/78 uppercase">
                {section.defaultCollapsed === undefined ? (
                  <span className="block px-[13px]">{section.label}</span>
                ) : (
                  <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => setFolded(key, !open)}
                    className="flex w-full items-center justify-between rounded-portal-button px-[13px] py-1 text-left tracking-[1.6px] uppercase hover:bg-white/14 hover:text-white"
                  >
                    {section.label}
                    <ChevronDownIcon aria-hidden className={cn('size-3 transition-transform', open ? '' : '-rotate-90')} />
                  </button>
                )}
              </h2>
            )}
            {open ? section.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={isExact(item)}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    'flex h-9 items-center rounded-portal-button px-[13px] text-portal-small transition-colors',
                    isActive
                      ? 'bg-white/14 text-white shadow-[inset_3px_0_var(--portal-brand-accent)]'
                      : 'text-white/78 hover:bg-white/14 hover:text-white',
                  )
                }
              >
                <span
                  aria-hidden="true"
                  className="inline-flex w-[25px] shrink-0 items-center [&>svg]:size-4"
                >
                  {item.icon}
                </span>
                {item.label}
              </NavLink>
            )) : null}
          </div>
          );
        })}
      </nav>

      <div className="flex-1" />

      {sidebarWidget}

      {onToggleCollapsed ? (
        <button
          type="button"
          onClick={onToggleCollapsed}
          className="mt-3 flex items-center gap-2 rounded-portal-button px-[13px] py-2 text-portal-small text-white/78 transition-colors hover:bg-white/14 hover:text-white"
        >
          <PanelLeftCloseIcon aria-hidden className="size-4 shrink-0" />
          Collapse
        </button>
      ) : null}
    </>
  );
}

export function Shell({
  identity,
  sections,
  account,
  sidebarWidget,
  topbarLeft,
  topbarRight,
  aside,
  banner,
  fillContent = false,
  children,
}: ShellProps): React.ReactElement {
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);
  const location = useLocation();
  const sidebarColor = identity.sidebarColor ?? 'var(--portal-brand-sidebar)';
  const toggleCollapsed = (): void => {
    setCollapsed((previous) => {
      const next = !previous;
      writeCollapsed(next);
      return next;
    });
  };

  // Close the drawer whenever the route changes or Escape is pressed — it must
  // never linger over the page it just navigated to.
  useEffect(() => setMenuOpen(false), [location.pathname]);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    // Lock the page behind the drawer so only the menu scrolls.
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  return (
    // overflow-x-clip is the backstop: no stray fixed-width child may widen
    // the canvas past the viewport (clip, not hidden — it must not become a
    // scroll container, or the sticky top bar would scroll with the page).
    <div className="flex min-h-screen overflow-x-clip">
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-10 hidden flex-col overflow-y-auto pt-[27px] pb-[18px] text-white min-[720px]:flex',
          collapsed ? 'w-sidebar-icons px-2' : 'w-sidebar-compact px-[18px] lg:w-sidebar',
        )}
        style={{ background: sidebarColor }}
      >
        <SidebarBody
          identity={identity}
          sections={sections}
          sidebarWidget={sidebarWidget}
          collapsed={collapsed}
          onToggleCollapsed={toggleCollapsed}
        />
      </aside>

      {/* Mobile navigation drawer — the hamburger's target below 720px. */}
      {menuOpen ? (
        <div className="fixed inset-0 z-30 min-[720px]:hidden">
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
            className="absolute inset-0 bg-black/40"
          />
          <div
            className="absolute inset-y-0 left-0 flex w-[82vw] max-w-[300px] flex-col overflow-y-auto px-[18px] pt-[22px] pb-[18px] text-white shadow-2xl"
            style={{ background: sidebarColor }}
          >
            <SidebarBody
              identity={identity}
              sections={sections}
              sidebarWidget={sidebarWidget}
              onNavigate={() => setMenuOpen(false)}
            />
          </div>
        </div>
      ) : null}

      <div
        className={cn(
          'min-w-0 flex-1',
          collapsed ? 'min-[720px]:ml-sidebar-icons' : 'min-[720px]:ml-sidebar-compact lg:ml-sidebar',
        )}
      >
        {/* z-10 vs the banner's z-8: both are sticky siblings, and the banner
            comes later in the DOM — at equal z it would paint over the account
            dropdown, which lives inside this header's stacking context. */}
        <header className="sticky top-0 z-10 flex h-topbar items-center justify-between gap-3 border-b border-portal-line bg-white px-4 min-[720px]:px-[34px]">
          {/* Hamburger — the only way to reach navigation below 720px. */}
          <button
            type="button"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
            className="-ml-1 grid size-9 shrink-0 place-items-center rounded-portal-button text-portal-ink hover:bg-portal-bg min-[720px]:hidden"
          >
            <svg aria-hidden="true" viewBox="0 0 20 20" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M3 5h14M3 10h14M3 15h14" />
            </svg>
          </button>

          <div className="hidden min-w-0 min-[720px]:block">{topbarLeft}</div>

          <div className="flex min-w-0 flex-1 items-center justify-end gap-2 self-stretch">
            {topbarRight}
            {account === undefined ? null : (
              // Constrained to the avatar alone on phones, where a name would
              // crowd the hamburger and the topbar actions out of the row. The
              // left border spans the full bar height, separating identity
              // from page actions.
              <div className="flex w-[46px] shrink-0 items-center self-stretch border-l border-portal-line pl-2 min-[720px]:w-auto min-[720px]:max-w-[240px] min-[720px]:pl-3">
                {account('topbar')}
              </div>
            )}
          </div>
        </header>

        {banner ? (
          <div className="sticky top-topbar z-8">{banner}</div>
        ) : null}

        <div className={cn('flex min-w-0', fillContent && 'h-[calc(100dvh-var(--spacing-topbar))]')}>
          <main
            className={cn(
              'min-w-0 flex-1',
              fillContent
                ? 'flex min-h-0 flex-col overflow-hidden'
                : 'mx-auto w-full max-w-content-max px-[4vw] pt-[35px] pb-[70px]',
            )}
          >
            {children}
          </main>
          {aside}
        </div>
      </div>
    </div>
  );
}

export interface PageHeadingProps {
  readonly eyebrow?: string;
  /**
   * Where the page sits, outermost first, in the eyebrow's style and in place
   * of it: "Administration / Teams". A list page ends with itself; a detail
   * page ends with its list, since the title names the record.
   */
  readonly breadcrumbs?: readonly BreadcrumbItem[];
  /** Detail pages only: where the "Back" link above the breadcrumbs goes. */
  readonly backTo?: string;
  readonly title: string;
  /** A line under the title; may carry links (e.g. a deal's account). */
  readonly subtitle?: React.ReactNode;
  readonly action?: React.ReactNode;
  /** 'compact' drops the title to 28px — for long, data-derived titles. */
  readonly size?: 'page' | 'compact';
  /** Anchors a tour step: rendered as `data-guide` on the root element. */
  readonly guide?: string | undefined;
}

/**
 * The blue uppercase section eyebrow that sits above a page title. Every
 * page heading carries one — use this directly on pages whose header layout
 * is too custom for {@link PageHeading} (actions, avatars, rich subtitles).
 *
 * @param props - The eyebrow text.
 * @returns The eyebrow.
 */
export function PageEyebrow({
  children,
}: {
  readonly children: React.ReactNode;
}): React.ReactElement {
  return (
    <p className="mb-2 text-portal-micro font-extrabold tracking-[1.2px] text-brand-link uppercase">
      {children}
    </p>
  );
}

/**
 * The page heading block from the mockup — eyebrow, 36px title, subtitle, and
 * an optional right-aligned action.
 *
 * @param props - Heading content.
 * @returns The heading.
 */
export function PageHeading({
  eyebrow,
  breadcrumbs,
  backTo,
  title,
  subtitle,
  action,
  size = 'page',
  guide,
}: PageHeadingProps): React.ReactElement {
  return (
    <div data-guide={guide} className="mb-[27px] flex max-w-full flex-col items-start justify-between gap-5 sm:flex-row sm:items-end">
      <div className="min-w-0">
        {backTo === undefined ? null : <BackLink to={backTo}>Back</BackLink>}
        {breadcrumbs === undefined ? (
          eyebrow ? <PageEyebrow>{eyebrow}</PageEyebrow> : null
        ) : (
          <Breadcrumbs items={breadcrumbs} className={backTo === undefined ? 'mb-2' : 'mt-3 mb-2'} />
        )}
        <Heading variant={size === 'compact' ? 'page-sm' : 'page'} as="h1" className="mb-2">
          {title}
        </Heading>
        {subtitle ? <p className="text-portal-small text-portal-muted">{subtitle}</p> : null}
      </div>
      {action ? <div className="max-w-full shrink-0 sm:whitespace-nowrap">{action}</div> : null}
    </div>
  );
}
